import { gql, useLazyQuery } from '@apollo/client';
import { translateBackend } from '@assets/i18n/config';
import type { AddressCountries, AddressJurisdiction } from '@generated/graphql';
import type { FileDocument } from '@interfaces/fileDocument';
import {
  type IdVerification,
  useCountrySpecificInformationSources,
} from '@interfaces/IdVerification';
import type { PhysicalAddress } from '@interfaces/physicalAddress';

import { ListItem } from '@mui/material';
import { AddressField } from '@ovComponents/2-component/addressField/addressField';
import { Autocomplete } from '@ovComponents/2-component/autocomplete/autocomplete';
import { Card } from '@ovComponents/2-component/card/card';
import { DateField } from '@ovComponents/2-component/dateField/dateField';
import { DroppableFileInput } from '@ovComponents/2-component/droppableFileInput/droppableFileInput';

import { MenuItem } from '@ovComponents/2-component/menuItem/menuItem';
import { SelectField } from '@ovComponents/2-component/selectField/selectField';
import { TextField } from '@ovComponents/2-component/textField/textField';
import { WorkflowCompletionForm } from '@ovComponents/4-module/workflowCompletion/components/workflowCompletionForm';
import { useClientContext } from '@pages/client/clientContext';
import { AnalyticsContext } from '@providers/analyticsProvider';
import { UserContext } from '@providers/userContextProvider';
import { ovAnalyticsEvents } from '@utils/analytics/analytics';
import { safeWindowOpen } from '@utils/urlSanitization';
import dayjs from 'dayjs';
import { Box, Grid, IconButton, Typography, useSnackbar } from 'hephaestus/web';
import { AttachFile, Delete } from 'hephaestus/webIcons';
import { kebabCase } from 'lodash-es';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { VALIDATE_AGE_OF_MAJORITY } from '../personalInformation/personalInformation.queries';

const isAddressEmpty = (address: PhysicalAddress | undefined) => {
  if (!address) return true;
  const requiredFields: (keyof PhysicalAddress)[] = [
    'city',
    'streetName',
    'postal',
    'country',
  ];

  return requiredFields.some((field) => !address[field]);
};

const getInvalidFieldsFromIdData = (
  idData: IdVerification,
  isUnderAgeOfMajority: boolean,
) => {
  const invalidFields: string[] = [];

  if (!idData.methodOfIDVerification) {
    invalidFields.push('methodOfIDVerification');
  }

  if (idData.methodOfIDVerification === 'DUAL_METHOD') {
    if (!idData.userName) {
      invalidFields.push('userName');
    }
    if (!idData.secondaryDocumentUserName) {
      invalidFields.push('secondaryDocumentUserName');
    }
    if (!idData.documentType) {
      invalidFields.push('documentType');
    }
    if (!idData.secondaryDocumentType) {
      invalidFields.push('secondaryDocumentType');
    }
    if (!idData.documentIssuingJurisdication) {
      invalidFields.push('documentIssuingJurisdication');
    }
    if (!idData.secondaryDocumentIssuingJurisdication) {
      invalidFields.push('secondaryDocumentIssuingJurisdication');
    }
    if (!idData.documentID) {
      invalidFields.push('documentID');
    }
    if (!idData.secondaryDocumentID) {
      invalidFields.push('secondaryDocumentID');
    }
    if (!idData.address || isAddressEmpty(idData.address)) {
      invalidFields.push('address');
    }
    if (!idData.secondaryDocumentDateOfBirth || isUnderAgeOfMajority) {
      invalidFields.push('secondaryDocumentDateOfBirth');
    }
  }

  if (
    idData.methodOfIDVerification &&
    ['CREDIT_CHECK', 'DIGITAL_DOCUMENT_CHECK', 'IN_PERSON_VALIDATION'].includes(
      idData.methodOfIDVerification,
    )
  ) {
    if (!idData.userName) {
      invalidFields.push('userName');
    }
    if (!idData.documentType) {
      invalidFields.push('documentType');
    }
    if (!idData.documentID) {
      invalidFields.push('documentID');
    }
    if (!idData.documentIssuingJurisdication) {
      invalidFields.push('documentIssuingJurisdication');
    }
    if (
      idData.methodOfIDVerification === 'CREDIT_CHECK' &&
      !idData.creditFileSource
    ) {
      invalidFields.push('creditFileSource');
    }
  }

  return invalidFields;
};

function Attachment({
  fileName,
  fileDocumentId,
  onDelete,
}: {
  fileName: string;
  fileDocumentId?: string;
  onDelete: () => void;
}) {
  const { enqueueSnackbar } = useSnackbar();

  const FETCH_FILE_DOCUMENT_URL = gql`
    query fetchFileDocumentDownloadUrl($fileDocumentId: ObjectID!) {
      fetchFileDocument(fileDocumentId: $fileDocumentId) {
        fileDocument {
          downloadUrl
        }
      }
    }
  `;

  const [fetchFileDocumentUrl] = useLazyQuery(FETCH_FILE_DOCUMENT_URL, {
    variables: { fileDocumentId },
    fetchPolicy: 'network-only',
    onCompleted: (dataDoc) =>
      setTimeout(() =>
        safeWindowOpen(
          dataDoc?.fetchFileDocument?.fileDocument.downloadUrl || '',
          {
            target: '_blank',
            onError: (errorMsg) => {
              enqueueSnackbar(errorMsg, { variant: 'error' });
            },
          },
        ),
      ),
  });

  const cardProps = fileDocumentId !== undefined && {
    hover: true,
    onClick: fetchFileDocumentUrl,
  };

  return (
    <Card sx={{ p: 1, mt: 1 }} {...cardProps}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <AttachFile />
          <Typography variant='bodySmall' sx={{ ml: 1 }}>
            {fileName}
          </Typography>
        </Box>
        <IconButton
          aria-label='delete'
          onClick={(e) => {
            e.stopPropagation();
            onDelete();
          }}
        >
          <Delete fontSize='inherit' />
        </IconButton>
      </Box>
    </Card>
  );
}

export const IdVerificationVisual = ({
  options,
  idDocumentTypes,
  idData,
  loading,
  updating,
  updateId,
  file1,
  setFile1,
  existingFileDocument1,
  file2,
  setFile2,
  existingFileDocument2,
  workflowCompletion,
  continueFunc,
  openPersona,
  featureFlagDocumentIdVerification,
}: {
  options: any;
  idDocumentTypes?: string[];
  idData: IdVerification & { idVerificationId?: string };
  loading: boolean;
  updating?: boolean;
  updateId: any;
  file1?: File;
  setFile1: (f?: File) => void;
  existingFileDocument1?: FileDocument;
  file2?: File;
  setFile2: (f?: File) => void;
  existingFileDocument2?: FileDocument;
  workflowCompletion?: any;
  continueFunc: () => void;
  openPersona: () => void;
  featureFlagDocumentIdVerification?: boolean;
}) => {
  const { t } = useTranslation(['client', 'shared']);
  const { sendAnalytic } = useContext(AnalyticsContext);
  const [focused, setFocused] = useState<string[]>([]);
  const informationSources = useCountrySpecificInformationSources();
  const clientContext = useClientContext();
  const { activeOrganization } = useContext(UserContext);
  const localizationOrg =
    (clientContext?.orgSettings?.applicableLocalization?.countries ??
      activeOrganization?.applicableLocalization?.countries)?.[0] ?? 'CA';

  const [validateAgeOfMajority] = useLazyQuery(VALIDATE_AGE_OF_MAJORITY);
  const [ageOfMajority, setAgeOfMajority] = useState(0);
  const [isUnderAgeOfMajority, setIsUnderAgeOfMajority] = useState(false);

  const messageIsUnderAgeOfMajority = t(
    'client:idVerifications.underAgeValidation',
  );

  const dateOfBirthValidation = (
    date: string | null | undefined,
  ): string | undefined => {
    if (!date) return undefined;

    if (isUnderAgeOfMajority) {
      return messageIsUnderAgeOfMajority.replace(
        '{{MINUMUM_AGE}}',
        ageOfMajority.toString(),
      );
    }
    return undefined;
  };

  const isSecondaryDocumentDateOfBirthFocused = focused.includes(
    'secondaryDocumentDateOfBirth',
  );
  const hasDateOfBirthError = useMemo(() => {
    if (!isSecondaryDocumentDateOfBirthFocused) return false;

    const date = idData?.secondaryDocumentDateOfBirth?.toString();
    return !date || isUnderAgeOfMajority;
  }, [
    isSecondaryDocumentDateOfBirthFocused,
    idData?.secondaryDocumentDateOfBirth,
    isUnderAgeOfMajority,
  ]);

  const getAllowedMethodsAndDocuments = useMemo(() => {
    // Get allowed methods
    const methods = [];
    if (
      options.allowVerificationMethodCreditCheck === undefined &&
      options.allowVerificationMethodDigitalDocumentCheck === undefined &&
      options.allowVerificationMethodDual === undefined &&
      options.allowVerificationMethodInPerson === undefined
    ) {
      return {
        methods: [
          'CREDIT_CHECK',
          'DIGITAL_DOCUMENT_CHECK',
          'DUAL_METHOD',
          'IN_PERSON_VALIDATION',
        ],

        documents: idDocumentTypes || [],
      };
    }

    if (
      options.allowVerificationMethodCreditCheck ||
      idData.methodOfIDVerification === 'CREDIT_CHECK'
    ) {
      methods.push('CREDIT_CHECK');
    }
    if (
      options.allowVerificationMethodDigitalDocumentCheck ||
      idData.methodOfIDVerification === 'DIGITAL_DOCUMENT_CHECK'
    ) {
      methods.push('DIGITAL_DOCUMENT_CHECK');
    }
    if (
      options.allowVerificationMethodDual ||
      idData.methodOfIDVerification === 'DUAL_METHOD'
    ) {
      methods.push('DUAL_METHOD');
    }
    if (
      options.allowVerificationMethodInPerson ||
      idData.methodOfIDVerification === 'IN_PERSON_VALIDATION'
    ) {
      methods.push('IN_PERSON_VALIDATION');
    }

    // Get available documents based on current method
    let availableDocuments = idDocumentTypes || [];

    const creditCheckDocumentTypes = options?.creditCheckDocumentTypes;
    const digitalMethodDocumentTypes = options?.digitalMethodDocumentTypes;
    const dualMethodDocumentTypes = options?.dualMethodDocumentTypes;
    const inPersonMethodDocumentTypes = options?.inPersonMethodDocumentTypes;

    if (idData.methodOfIDVerification) {
      switch (idData.methodOfIDVerification) {
        case 'CREDIT_CHECK':
          availableDocuments =
            creditCheckDocumentTypes && featureFlagDocumentIdVerification
              ? [creditCheckDocumentTypes]
              : availableDocuments;
          break;
        case 'DIGITAL_DOCUMENT_CHECK':
          availableDocuments =
            digitalMethodDocumentTypes && featureFlagDocumentIdVerification
              ? digitalMethodDocumentTypes
              : availableDocuments;
          break;
        case 'DUAL_METHOD':
          availableDocuments =
            dualMethodDocumentTypes && featureFlagDocumentIdVerification
              ? dualMethodDocumentTypes
              : availableDocuments;
          break;
        case 'IN_PERSON_VALIDATION':
          availableDocuments =
            inPersonMethodDocumentTypes && featureFlagDocumentIdVerification
              ? inPersonMethodDocumentTypes
              : availableDocuments;
          break;
      }
    }

    return {
      methods,
      documents: Array.isArray(availableDocuments)
        ? [...availableDocuments].sort((a, b) =>
            a.toLowerCase().localeCompare(b.toLowerCase()),
          )
        : [],
    };
  }, [options, idData, idDocumentTypes]);

  // auto select if there's only one option
  useEffect(() => {
    if (getAllowedMethodsAndDocuments.methods.length === 1) {
      updateId({
        ...idData,
        methodOfIDVerification: getAllowedMethodsAndDocuments.methods[0],
      });
    }
  }, []);

  const submit = async () => {
    if (options?.manualVerification) {
      if (!validate()) {
        return;
      }
      sendAnalytic(
        ovAnalyticsEvents.workflowsIdVerificationContinueButtonSelect,
        {
          workflowStepTitle: options?.title,
          workflowId: workflowCompletion?.workflow?.id,
          workflowName: workflowCompletion?.workflow?.name,
          activeWorkflowCompletionId: workflowCompletion?.id,
          objectId: workflowCompletion?.objectId,
          objectType: workflowCompletion?.objectType,
        },
      );
      await continueFunc();
    }
    if (!options?.manualVerification && options?.automatedVerification) {
      sendAnalytic(
        ovAnalyticsEvents.workflowsIdVerificationContinueButtonSelect,
        {
          workflowStepTitle: options?.title,
          workflowId: workflowCompletion?.workflow?.id,
        },
      );
      await openPersona();
    }
  };

  const validate = () => {
    const invalidFields = getInvalidFieldsFromIdData(
      idData,
      isUnderAgeOfMajority,
    );
    setFocused(invalidFields);
    return invalidFields.length === 0;
  };

  useEffect(() => {
    if (!focused.includes('secondaryDocumentDateOfBirth')) return;
    if (!idData.secondaryDocumentDateOfBirth) {
      setIsUnderAgeOfMajority(false);
      return;
    }

    const country = idData.address?.country || localizationOrg;
    const jurisdiction = idData.address?.jurisdiction;

    if (!jurisdiction || !country) return;

    const validateAge = async () => {
      const result = await validateAgeOfMajority({
        variables: {
          dateOfBirth: dayjs(idData.secondaryDocumentDateOfBirth).format(
            'YYYY-MM-DD',
          ),
          country: country as unknown as AddressCountries, // The two types are exactly the same
          jurisdiction: jurisdiction as unknown as AddressJurisdiction, // The two types are exactly the same
        },
      });

      if (result.data) {
        setIsUnderAgeOfMajority(!result.data.validateAgeOfMajority.valid);
        setAgeOfMajority(result.data.validateAgeOfMajority.ageOfMajority);
      }
    };

    validateAge();
  }, [
    idData.secondaryDocumentDateOfBirth,
    idData.address,
    focused,
    localizationOrg,
    validateAgeOfMajority,
  ]);

  const translateJurisdictionString = (option: string): string => {
    if (informationSources.includes(option)) {
      return t(`client:edit.jurisdicationOptionsForUsandCanada.${option}`);
    }
    return option;
  };

  const renderFileUploadSection = (
    existingFileDocument: FileDocument | undefined,
    file: File | undefined,
    setFile: (f?: File) => void,
  ) => (
    <ListItem>
      <Grid container>
        <Grid size={12}>
          {!existingFileDocument && !file && (
            <DroppableFileInput onFileChosen={setFile} disabled={updating} />
          )}
          {existingFileDocument?.id && (
            <Attachment
              fileName={existingFileDocument.fileName}
              fileDocumentId={existingFileDocument.id}
              onDelete={() => {
                setFile(undefined);
              }}
            />
          )}
          {file && (
            <Attachment
              fileName={file.name}
              onDelete={() => {
                setFile(undefined);
              }}
            />
          )}
        </Grid>
      </Grid>
    </ListItem>
  );

  const renderVerifiedDateField = (isDisabled = false, isSecondary = false) => (
    <ListItem>
      <Grid container>
        <Grid size={12}>
          <DateField
            dataTestId={`custom-date-picker-${isSecondary ? 'secondary-' : ''}verified-date`}
            onChange={
              isDisabled
                ? undefined
                : (date: any) =>
                    updateId({
                      ...idData,
                      [isSecondary
                        ? 'secondaryDocumentVerifiedDate'
                        : 'verifiedDate']: dayjs(date?.toString()).format(
                        'YYYY-MM-DD',
                      ),
                    })
            }
            disabled={isDisabled || updating}
            label={t(
              isSecondary
                ? 'idVerifications.secondaryDocumentVerifiedDate'
                : 'verifiedDate',
            )}
            fullWidth
            value={
              isDisabled
                ? dayjs()
                : idData[
                    isSecondary
                      ? 'secondaryDocumentVerifiedDate'
                      : 'verifiedDate'
                  ] || dayjs().format('YYYY-MM-DD')
            }
            openTo='day'
          />
        </Grid>
      </Grid>
    </ListItem>
  );

  const hasIdVerification = idData.idVerificationId !== undefined;
  return (
    <WorkflowCompletionForm
      onSubmit={submit}
      testId='id-verification-form'
      submitButtonProps={{
        label: t('continue'),
        disabled: loading || updating,
        dataTestId: `id-verification-${kebabCase(translateBackend(options.title))}-continue-button`,
      }}
    >
      <Typography variant='displayLarge' sx={{ mt: 1 }}>
        {translateBackend(options?.title)}
      </Typography>
      <Typography variant='bodyLarge' sx={{ mb: 3 }}>
        <ReactMarkdown linkTarget='_blank' remarkPlugins={[remarkGfm]}>
          {translateBackend(options?.subtitle)}
        </ReactMarkdown>
      </Typography>

      {options?.manualVerification && (
        <>
          <ListItem>
            <Grid container>
              <Grid size={12}>
                <SelectField
                  testId='method-of-id-verification'
                  onChange={(e: any) => {
                    updateId({
                      ...idData,
                      methodOfIDVerification: e.target.value,
                      documentType: '',
                    });
                    setFocused((prev) =>
                      prev.filter((f) => f !== 'documentType'),
                    );
                  }}
                  disabled={loading || updating || hasIdVerification}
                  label={t('methodOfIDVerification')}
                  fullWidth
                  sx={{ mt: 2 }}
                  value={idData.methodOfIDVerification}
                  onBlur={() => {
                    if (!focused.includes('methodOfIDVerification')) {
                      setFocused([...focused, 'methodOfIDVerification']);
                    }
                  }}
                  error={
                    !idData.methodOfIDVerification &&
                    focused.includes('methodOfIDVerification')
                  }
                >
                  {getAllowedMethodsAndDocuments.methods.map((x: string) => (
                    <MenuItem
                      key={x}
                      value={x}
                      data-testid={`document-type-${x.toLowerCase()}`}
                    >
                      {t(`idVerifications.idVerificationMethods.${x}`)}
                    </MenuItem>
                  ))}
                </SelectField>
              </Grid>
            </Grid>
          </ListItem>

          {idData.methodOfIDVerification && !loading && (
            <>
              {idData.methodOfIDVerification !== 'DUAL_METHOD' ? (
                <ListItem>
                  <Typography sx={{ mt: 2 }} variant='headingSmall'>
                    {t('documentOne')}
                  </Typography>
                </ListItem>
              ) : (
                <ListItem>
                  <Typography sx={{ mt: 2 }} variant='headingSmall'>
                    {t('idVerifications.documentOneAddress')}
                  </Typography>
                </ListItem>
              )}
              <ListItem>
                <Grid container>
                  <Grid size={12}>
                    <TextField
                      testId={'client-name-on-document'}
                      onChange={(e: any) =>
                        updateId({ ...idData, userName: e.target.value })
                      }
                      disabled={updating}
                      label={t('idVerifications.clientNameOnDocument')}
                      fullWidth
                      value={idData.userName}
                      onBlur={() => setFocused([...focused, 'userName'])}
                      error={!idData.userName && focused.includes('userName')}
                    />
                  </Grid>
                </Grid>
              </ListItem>

              <ListItem>
                <Grid container>
                  <Grid size={12}>
                    <Grid
                      container
                      spacing={2}
                      sx={{
                        justifyContent: 'space-between',
                      }}
                    >
                      <Grid size={6}>
                        <SelectField
                          testId={'information-type'}
                          onChange={(e: any) =>
                            updateId({
                              ...idData,
                              documentType: e.target.value,
                            })
                          }
                          disabled={updating}
                          label={t('idVerifications.documentType')}
                          fullWidth
                          value={idData.documentType}
                          onBlur={() =>
                            setFocused([...focused, 'documentType'])
                          }
                          error={
                            !idData.documentType &&
                            focused.includes('documentType')
                          }
                        >
                          {getAllowedMethodsAndDocuments.documents.map(
                            (x: string) => (
                              <MenuItem
                                key={x}
                                value={x}
                                data-testid={`document-type-${kebabCase(x)}`}
                              >
                                {t(`documentTypes.${x}`)}
                              </MenuItem>
                            ),
                          )}
                        </SelectField>
                      </Grid>
                      <Grid size={6}>
                        <Autocomplete
                          fullWidth
                          testId='information-source'
                          label={t(
                            'idVerifications.documentIssuingJurisdication',
                          )}
                          value={idData.documentIssuingJurisdication}
                          disabled={updating}
                          freeSolo
                          onInputChange={(_event: any, value: any) =>
                            updateId({
                              ...idData,
                              documentIssuingJurisdication: value,
                            })
                          }
                          onBlur={() =>
                            setFocused([
                              ...focused,
                              'documentIssuingJurisdication',
                            ])
                          }
                          error={
                            !idData.documentIssuingJurisdication &&
                            focused.includes('documentIssuingJurisdication')
                          }
                          options={informationSources.map(
                            translateJurisdictionString,
                          )}
                          renderOption={(props: any, option: any) => (
                            <li
                              {...props}
                              data-testid={`information-source-option-${kebabCase(option)}`}
                            >
                              {option}
                            </li>
                          )}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </ListItem>
              <ListItem>
                <Grid container>
                  <Grid size={12}>
                    <Grid
                      container
                      spacing={2}
                      sx={{
                        justifyContent: 'space-between',
                      }}
                    >
                      <Grid size={6}>
                        <TextField
                          testId='document-reference-number'
                          onChange={(e: any) =>
                            updateId({ ...idData, documentID: e.target.value })
                          }
                          disabled={updating}
                          label={t('idVerifications.documentID')}
                          fullWidth
                          value={idData.documentID}
                          onBlur={() => setFocused([...focused, 'documentID'])}
                          error={
                            !idData.documentID && focused.includes('documentID')
                          }
                        />
                      </Grid>
                      <Grid size={6}>
                        <DateField
                          onChange={(date: any) =>
                            updateId({
                              ...idData,
                              documentExpiryDate: dayjs(
                                date?.toString(),
                              ).format('YYYY-MM-DD'),
                            })
                          }
                          disabled={updating}
                          label={t('idVerifications.expiryDate')}
                          fullWidth
                          value={idData.documentExpiryDate}
                          onBlur={() =>
                            setFocused([...focused, 'documentExpiryDate'])
                          }
                          error={
                            !idData.documentExpiryDate &&
                            focused.includes('documentExpiryDate')
                          }
                          minDate={dayjs()}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </ListItem>

              {idData.methodOfIDVerification !== 'DUAL_METHOD' && (
                <>
                  {renderFileUploadSection(
                    existingFileDocument1,
                    file1,
                    setFile1,
                  )}
                  {renderVerifiedDateField(!options?.verifiedDate)}
                </>
              )}

              {idData.methodOfIDVerification === 'CREDIT_CHECK' && (
                <ListItem>
                  <Grid container>
                    <Grid size={12}>
                      <TextField
                        testId='credit-file-source'
                        onChange={(e: any) =>
                          updateId({
                            ...idData,
                            creditFileSource: e.target.value,
                          })
                        }
                        disabled={updating}
                        label={t('idVerifications.creditFileSource')}
                        fullWidth
                        value={idData.creditFileSource}
                        onBlur={() =>
                          setFocused([...focused, 'creditFileSource'])
                        }
                        error={
                          !idData.creditFileSource &&
                          focused.includes('creditFileSource')
                        }
                      />
                    </Grid>
                  </Grid>
                </ListItem>
              )}

              {idData.methodOfIDVerification === 'DUAL_METHOD' && (
                <>
                  <ListItem>
                    <Grid container>
                      <Grid size={12}>
                        <Grid
                          container
                          spacing={2}
                          sx={{
                            justifyContent: 'space-between',
                          }}
                        >
                          <Grid size={12}>
                            <AddressField
                              address={idData?.address as PhysicalAddress}
                              onChange={(e: any) =>
                                updateId({ ...idData, address: e })
                              }
                              label={t('idVerifications.address')}
                              onFocus={() =>
                                setFocused([...focused, 'address'])
                              }
                              error={
                                isAddressEmpty(
                                  idData?.address as PhysicalAddress,
                                ) && focused.includes('address')
                              }
                            />
                          </Grid>
                        </Grid>
                      </Grid>
                    </Grid>
                  </ListItem>
                  {renderVerifiedDateField(!options?.verifiedDate)}
                  {renderFileUploadSection(
                    existingFileDocument1,
                    file1,
                    setFile1,
                  )}
                  <ListItem>
                    <Typography sx={{ mt: 2 }} variant='headingSmall'>
                      {t('idVerifications.documentTwoDateOfBirth')}
                    </Typography>
                  </ListItem>
                  <ListItem>
                    <Grid container>
                      <Grid size={12}>
                        <TextField
                          testId='client-name-on-document-secondary'
                          onChange={(e: any) =>
                            updateId({
                              ...idData,
                              secondaryDocumentUserName: e.target.value,
                            })
                          }
                          disabled={updating}
                          label={t('idVerifications.clientNameOnDocument')}
                          fullWidth
                          value={idData.secondaryDocumentUserName}
                          onBlur={() =>
                            setFocused([
                              ...focused,
                              'secondaryDocumentUserName',
                            ])
                          }
                          error={
                            !idData.secondaryDocumentUserName &&
                            focused.includes('secondaryDocumentUserName')
                          }
                        />
                      </Grid>
                    </Grid>
                  </ListItem>

                  <ListItem>
                    <Grid container>
                      <Grid size={12}>
                        <Grid
                          container
                          spacing={2}
                          sx={{
                            justifyContent: 'space-between',
                          }}
                        >
                          <Grid size={6}>
                            <SelectField
                              testId='information-type-secondary'
                              onChange={(e: any) =>
                                updateId({
                                  ...idData,
                                  secondaryDocumentType: e.target.value,
                                })
                              }
                              disabled={updating}
                              label={t('idVerifications.secondaryDocumentType')}
                              fullWidth
                              value={idData.secondaryDocumentType}
                              onBlur={() =>
                                setFocused([
                                  ...focused,
                                  'secondaryDocumentType',
                                ])
                              }
                              error={
                                !idData.secondaryDocumentType &&
                                focused.includes('secondaryDocumentType')
                              }
                            >
                              {getAllowedMethodsAndDocuments.documents.map(
                                (x: string) => (
                                  <MenuItem
                                    key={x}
                                    value={x}
                                    data-testid={`document-type-${kebabCase(x)}`}
                                  >
                                    {t(`documentTypes.${x}`)}
                                  </MenuItem>
                                ),
                              )}
                            </SelectField>
                          </Grid>
                          <Grid size={6}>
                            <Autocomplete
                              fullWidth
                              testId='secondaryDocumentIssuingJurisdication'
                              label={t(
                                'idVerifications.secondaryDocumentIssuingJurisdication',
                              )}
                              value={
                                idData.secondaryDocumentIssuingJurisdication
                              }
                              disabled={updating}
                              freeSolo
                              onInputChange={(_event: any, value: any) =>
                                updateId({
                                  ...idData,
                                  secondaryDocumentIssuingJurisdication: value,
                                })
                              }
                              onBlur={() =>
                                setFocused([
                                  ...focused,
                                  'secondaryDocumentIssuingJurisdication',
                                ])
                              }
                              error={
                                !idData.secondaryDocumentIssuingJurisdication &&
                                focused.includes(
                                  'secondaryDocumentIssuingJurisdication',
                                )
                              }
                              options={informationSources.map(
                                translateJurisdictionString,
                              )}
                              renderOption={(props: any, option: any) => (
                                <li
                                  {...props}
                                  data-testid={`information-source-option-${kebabCase(option)}`}
                                >
                                  {option}
                                </li>
                              )}
                            />
                          </Grid>
                        </Grid>
                      </Grid>
                    </Grid>
                  </ListItem>

                  <ListItem>
                    <Grid container>
                      <Grid size={12}>
                        <Grid
                          container
                          spacing={2}
                          sx={{
                            justifyContent: 'space-between',
                          }}
                        >
                          <Grid size={6}>
                            <TextField
                              testId='document-reference-number-secondary'
                              onChange={(e: any) =>
                                updateId({
                                  ...idData,
                                  secondaryDocumentID: e.target.value,
                                })
                              }
                              disabled={updating}
                              label={t('idVerifications.secondaryDocumentID')}
                              fullWidth
                              value={idData.secondaryDocumentID}
                              onBlur={() =>
                                setFocused([...focused, 'secondaryDocumentID'])
                              }
                              error={
                                !idData.secondaryDocumentID &&
                                focused.includes('secondaryDocumentID')
                              }
                            />
                          </Grid>
                          <Grid size={6}>
                            <DateField
                              dataTestId={'custom-date-picker-secondary'}
                              onChange={(date: any) =>
                                updateId({
                                  ...idData,
                                  secondaryDocumentExpiryDate: dayjs(
                                    date?.toString(),
                                  ).format('YYYY-MM-DD'),
                                })
                              }
                              disabled={updating}
                              label={t(
                                'idVerifications.secondaryDocumentExpiryDate',
                              )}
                              fullWidth
                              minDate={dayjs()}
                              value={idData.secondaryDocumentExpiryDate}
                              onBlur={() =>
                                setFocused([
                                  ...focused,
                                  'secondaryDocumentExpiryDate',
                                ])
                              }
                              error={
                                !idData.secondaryDocumentExpiryDate &&
                                focused.includes('secondaryDocumentExpiryDate')
                              }
                            />
                          </Grid>
                        </Grid>
                      </Grid>
                    </Grid>
                  </ListItem>

                  <ListItem>
                    <Grid container>
                      <Grid size={12}>
                        <DateField
                          onChange={(date: any) =>
                            updateId({
                              ...idData,
                              secondaryDocumentDateOfBirth: dayjs(
                                date?.toString(),
                              ).format('YYYY-MM-DD'),
                            })
                          }
                          disabled={updating}
                          label={t(
                            'idVerifications.secondaryDocumentDateOfBirth',
                          )}
                          fullWidth
                          value={idData.secondaryDocumentDateOfBirth}
                          onBlur={() =>
                            setFocused([
                              ...focused,
                              'secondaryDocumentDateOfBirth',
                            ])
                          }
                          error={hasDateOfBirthError}
                          errorText={dateOfBirthValidation(
                            idData?.secondaryDocumentDateOfBirth?.toString(),
                          )}
                          maxDate={dayjs()}
                        />
                      </Grid>
                    </Grid>
                  </ListItem>
                  {options?.verifiedDate
                    ? renderVerifiedDateField(false, true)
                    : renderVerifiedDateField(true, true)}
                  <ListItem>
                    <Grid container>
                      <Grid size={12}>
                        {!existingFileDocument2 && !file2 && (
                          <DroppableFileInput
                            onFileChosen={setFile2}
                            disabled={updating}
                          />
                        )}
                        {existingFileDocument2?.id && (
                          <Attachment
                            fileName={existingFileDocument2.fileName}
                            fileDocumentId={existingFileDocument2.id}
                            onDelete={() => {
                              setFile2(undefined);
                            }}
                          />
                        )}
                        {file2 && (
                          <Attachment
                            fileName={file2.name}
                            onDelete={() => {
                              setFile2(undefined);
                            }}
                          />
                        )}
                      </Grid>
                    </Grid>
                  </ListItem>
                </>
              )}
            </>
          )}
        </>
      )}

      {!options?.manualVerification && options?.automatedVerification && (
        <Typography
          variant='bodyLarge'
          component='div'
          sx={{ mb: 3, table: { width: '100%' } }}
        >
          <ReactMarkdown remarkPlugins={[remarkGfm]} linkTarget={'_blank'}>
            {translateBackend(options?.automatedVerificationText)}
          </ReactMarkdown>
        </Typography>
      )}
    </WorkflowCompletionForm>
  );
};

export default IdVerificationVisual;
